import { Button, Input, Text, TextProps } from "@ui-kitten/components";
import React from "react";
import { Image, StyleSheet, View } from "react-native";
import { Controller, useForm } from "react-hook-form";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";
import i18next from "../../i18n/i18n";
import imageSource from "../../assets/undraw_verified_re_4io7.svg";
import { AppNavigatorScreenProps } from "../../navigators/AppNavigatorScreenProps";
import LoadingButton from "../../components/LoadingButton";
import activeCode from "../../services/activeCode";
import isErrorResponse from "../../services/isErrorResponse";
import ValidatorErrorCode from "../../services/ValidatorErrorCode";
import AvailableLicenses from "./components/AvailableLicenses";
import logError from "../../helpers/logError";
import ScreenContainer from "../../components/ScreenContainer";
import AnalyticsEventKey from "../../utils/analytics/analyticsEventsKeys";
import LogAnalyticsEvent from "../../utils/analytics/LogAnalyticsEvent";

type FormInput = {
    code: string;
};

const HomeScreen: React.FC<AppNavigatorScreenProps<"HomeScreen">> = ({
    navigation: { navigate },
}) => {
    const {
        control,
        handleSubmit,
        setError,
        setValue,
        formState: { isValid, isDirty },
    } = useForm<FormInput>({
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const [isLoading, setIsLoading] = React.useState(false);
    const { t } = useTranslation();
    const onSubmit = React.useCallback(
        (data: FormInput) => {
            setIsLoading(true);
            activeCode(data)
                .then((d) => {
                    if (isErrorResponse(d.data)) {
                        switch (d.data.code) {
                            case ValidatorErrorCode.WRONG_CODE:
                                setError("code", {
                                    type: "validate",
                                    message: i18next.t("home.codeUsed"),
                                });
                                return;
                            default:
                                logError("unknown error response", d.data);
                                setError("code", {
                                    type: "validate",
                                    message: i18next.t("errors.internal"),
                                });
                                return;
                        }
                    }
                    LogAnalyticsEvent({
                        key: AnalyticsEventKey.CodeActived,
                        data: {
                            code: data.code,
                            message: d.data.message,
                            tokensAdded: d.data.tokensAdded,
                        },
                    });
                    setError("code", {
                        type: "success",
                        message: i18next.t("home.codeAccepted"),
                    });
                    setValue("code", "");
                })
                .catch((error) => {
                    logError(error);
                    setError("code", {
                        type: "validate",
                        message: i18next.t("errors.internal"),
                    });
                })
                .finally(() => setIsLoading(false));
        },
        [setError, setValue],
    );
    return (
        <ScreenContainer contentContainerStyle={styles.container}>
            <Image style={styles.image} source={imageSource as any} />
            <AvailableLicenses />
            <View style={styles.hintMargin}>
                <Text category="h6" style={styles.hintMargin} appearance="hint">
                    {t("home.enterCode")}
                </Text>
            </View>
            <View style={styles.formContainer}>
                <Controller
                    control={control}
                    render={({
                        field: { onChange, onBlur, value },
                        formState: { errors },
                    }) => (
                        <View style={styles.inputMargin}>
                            <Input
                                placeholder={t("labels.codeExample")}
                                value={value}
                                status={
                                    errors.code?.type === "validate"
                                        ? "danger"
                                        : "basic"
                                }
                                onBlur={onBlur}
                                onChangeText={(nextValue) =>
                                    onChange(nextValue)
                                }
                            />
                            {errors.code?.type === "validate" && (
                                <InputMessages
                                    status="danger"
                                    messages={errors.code.message}
                                />
                            )}
                            {errors.code?.type === "success" && (
                                <InputMessages
                                    status="success"
                                    messages={errors.code.message}
                                />
                            )}
                        </View>
                    )}
                    name="code"
                    defaultValue=""
                    rules={{ required: true }}
                />

                <View style={styles.inputMargin}>
                    <LoadingButton
                        isLoading={isLoading}
                        disabled={!isValid || !isDirty}
                        onPress={handleSubmit(onSubmit)}
                    >
                        {t("home.activateCode")}
                    </LoadingButton>
                </View>
                <View style={styles.inputMargin}>
                    <Button
                        appearance="outline"
                        status="info"
                        onPress={() => {
                            navigate("PaymentScreen");
                        }}
                    >
                        {t("labels.payment")}
                    </Button>
                </View>
                <View style={styles.inputMargin}>
                    <Button
                        appearance="ghost"
                        onPress={async () => {
                            await getAuth().signOut();
                        }}
                    >
                        {t("labels.logout")}
                    </Button>
                </View>
                <View style={styles.inputMargin}>
                    <Button
                        appearance="ghost"
                        onPress={() => {
                            navigate("ChangePasswordScreen");
                        }}
                    >
                        {t("labels.changePassword")}
                    </Button>
                </View>
            </View>
        </ScreenContainer>
    );
};

const InputMessages: React.FC<{
    messages?: string;
    status?: TextProps["status"];
}> = ({ messages, status }) => {
    return (
        <View style={styles.errorMessage}>
            <Text style={styles.errorMessage} category="c1" status={status}>
                {messages}
            </Text>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        marginTop: 30,
        flex: 1,
        alignItems: "center",
    },
    titleMarginTop: {
        marginTop: 30,
    },
    hintMargin: {
        marginTop: 8,
    },
    image: {
        height: 329,
        width: 317,
        resizeMode: "contain",
    },
    inputMargin: {
        marginTop: 15,
    },
    errorMessage: {
        marginTop: 4,
    },
    formContainer: {
        minWidth: 317,
    },
});

export default HomeScreen;
